<template>
    <div class="modal-container">
        <form @submit.prevent="handleSubmit" class="modal-form">
            <div class="modal-header">
                <div class="icon-container">
                    <PhIdentificationBadge :size="24" class="text-gray-500" />
                </div>
                <div class="header-text">
                    <h2>{{ isEditMode ? 'Edit Alternate Name' : 'Add Alternate Name' }}</h2>
                    <p>{{ isEditMode ? 'Update this alternate name (AKA)' : 'Add an alternate name (AKA) for this employee' }}</p>
                </div>
            </div>

            <div class="modal-body">
                <div class="form-row">
                    <label>First Name</label>
                    <PrimeInputText
                        v-model="formData.first_name"
                        placeholder="First name"
                        class="w-full"
                    />
                </div>

                <div class="form-row">
                    <label>Middle Name</label>
                    <PrimeInputText
                        v-model="formData.middle_name"
                        placeholder="Middle name (optional)"
                        class="w-full"
                    />
                </div>

                <div class="form-row">
                    <label>Last Name</label>
                    <PrimeInputText
                        v-model="formData.last_name"
                        placeholder="Last name"
                        class="w-full"
                    />
                </div>
            </div>

            <div class="modal-footer">
                <PrimeButton label="Cancel" @click="closeDialog" class="p-btn-root_secondary" :disabled="isLoading" />
                <PrimeButton :label="isEditMode ? 'Save Changes' : 'Add Name'" type="submit" :loading="isLoading" />
            </div>
        </form>
    </div>
</template>

<script setup lang="ts">
import { PhIdentificationBadge } from '@phosphor-icons/vue';
import { businessService } from '~/services/business-service';
import { ModalNames } from '~/types/modals';
import { memberFacingMessage } from '~/utils/errors';

const dialogRef = getDialogRef(ModalNames.ADD_EMPLOYEE_AKA);
const { showSuccess, showError } = useGlobalToast();
const isLoading = ref(false);

const companyId = computed(() => dialogRef.value?.data?.companyId);
const employeeId = computed(() => dialogRef.value?.data?.employeeId);
// When opener passes an `aka` payload we're in edit mode; otherwise add.
const editingAka = computed(() => dialogRef.value?.data?.aka ?? null);
const isEditMode = computed(() => editingAka.value !== null);

const formData = reactive({
    first_name: editingAka.value?.first_name ?? '',
    middle_name: editingAka.value?.middle_name ?? '',
    last_name: editingAka.value?.last_name ?? '',
});

const closeDialog = () => {
    dialogRef.value.close();
};

const handleSubmit = async () => {
    if (!formData.first_name || !formData.last_name) {
        showError('Error', 'First and last name are required');
        return;
    }

    isLoading.value = true;
    try {
        const payload = {
            first_name: formData.first_name,
            middle_name: formData.middle_name || undefined,
            last_name: formData.last_name,
        };
        if (isEditMode.value && editingAka.value) {
            await businessService.updateEmployeeAka(
                companyId.value,
                employeeId.value,
                editingAka.value.id,
                payload,
            );
            showSuccess('Success', 'Alternate name updated successfully');
        } else {
            await businessService.addEmployeeAka(companyId.value, employeeId.value, payload);
            showSuccess('Success', 'Alternate name added successfully');
        }
        dialogRef.value.close({ updated: true });
    } catch (error) {
        showError('Error', memberFacingMessage(error, 'Failed to save alternate name'));
    } finally {
        isLoading.value = false;
    }
};
</script>

<style lang="scss" scoped>
@use '../../../assets/styles/business-form-modal';
</style>
